import { useState } from "react";
import { format } from "date-fns";
import { cs, enUS } from "date-fns/locale";
import { CalendarDays, X } from "lucide-react";
import type { DateRange } from "react-day-picker";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useLocalization } from "@/context/localization-context";
import type { TranslationKey } from "@/i18n/messages";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";

const startOfDay = (date: Date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
};
const endOfDay = (date: Date) => {
  const d = new Date(date);
  d.setHours(23, 59, 59, 999);
  return d;
};
const daysAgo = (days: number) => {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d;
};

/** labels are translation keys, translated when rendered */
const PRESETS: { label: TranslationKey; range: () => { from: Date; to: Date } }[] = [
  { label: "common.today", range: () => ({ from: startOfDay(new Date()), to: endOfDay(new Date()) }) },
  { label: "common.yesterday", range: () => ({ from: startOfDay(daysAgo(1)), to: endOfDay(daysAgo(1)) }) },
  { label: "common.last7Days", range: () => ({ from: startOfDay(daysAgo(6)), to: endOfDay(new Date()) }) },
  { label: "common.last30Days", range: () => ({ from: startOfDay(daysAgo(29)), to: endOfDay(new Date()) }) },
];

const sameDay = (a?: Date, b?: Date) => !!a && !!b && a.toDateString() === b.toDateString();

interface DateRangeFilterProps {
  from?: Date;
  to?: Date;
  /** the end is the end of its day; undefined clears the filter */
  onChange: (from: Date | undefined, to: Date | undefined) => void;
  placeholder?: string;
  className?: string;
}

/** Toolbar date filter: one-click presets next to a calendar for any range, clearable in place. */
export function DateRangeFilter({
  from,
  to,
  onChange,
  placeholder,
  className,
}: DateRangeFilterProps) {
  const [open, setOpen] = useState(false);
  const { locale, t } = useLocalization();
  const narrow = useIsMobile(640);
  const dateLocale = locale === "cs" ? cs : enUS;
  const thisYear = (date: Date) => date.getFullYear() === new Date().getFullYear();
  const short = (date: Date) =>
    format(
      date,
      locale === "cs" ? (thisYear(date) ? "d. M." : "d. M. y") : thisYear(date) ? "MMM d" : "MMM d, y",
      { locale: dateLocale },
    );

  const preset = PRESETS.find((p) => {
    const range = p.range();
    return sameDay(range.from, from) && sameDay(range.to, to);
  });
  const label = preset
    ? t(preset.label)
    : from && to
      ? sameDay(from, to)
        ? short(from)
        : `${short(from)} – ${short(to)}`
      : from
        ? t("common.from", { date: short(from) })
        : to
          ? t("common.until", { date: short(to) })
          : (placeholder ?? t("common.anyTime"));
  const active = !!(from || to);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <div className={cn("relative flex", className)}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className={cn(
              "h-9 w-full justify-start gap-2 font-normal",
              active ? "pr-8 text-foreground" : "text-muted-foreground",
            )}
          >
            <CalendarDays className="h-4 w-4" />
            <span className="truncate">{label}</span>
          </Button>
        </PopoverTrigger>
        {active && (
          <button
            type="button"
            onClick={() => onChange(undefined, undefined)}
            className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
            aria-label={t("common.clearDateFilter")}
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      <PopoverContent
        align="start"
        className="flex w-auto max-w-[calc(100vw-1.5rem)] flex-col gap-0 p-0 sm:flex-row"
      >
        <div className="flex flex-wrap gap-1 border-b p-2 sm:w-36 sm:flex-col sm:flex-nowrap sm:border-b-0 sm:border-r">
          {PRESETS.map((p) => (
            <Button
              key={p.label}
              variant={preset === p ? "secondary" : "ghost"}
              size="sm"
              className="justify-start"
              onClick={() => {
                const range = p.range();
                onChange(range.from, range.to);
                setOpen(false);
              }}
            >
              {t(p.label)}
            </Button>
          ))}
          <Button
            variant={!active ? "secondary" : "ghost"}
            size="sm"
            className="justify-start"
            onClick={() => {
              onChange(undefined, undefined);
              setOpen(false);
            }}
          >
            {t("common.anyTime")}
          </Button>
        </div>
        <Calendar
          initialFocus
          mode="range"
          defaultMonth={from ?? (narrow ? new Date() : daysAgo(30))}
          selected={{ from, to } as DateRange}
          onSelect={(range) =>
            onChange(range?.from ? startOfDay(range.from) : undefined, range?.to ? endOfDay(range.to) : undefined)
          }
          numberOfMonths={narrow ? 1 : 2}
          locale={dateLocale}
        />
      </PopoverContent>
    </Popover>
  );
}
